import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    Checkbox,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    ListItemText,
    Chip,
    Stack
} from "@mui/material";

import {
    collection,
    getDocs,
    query,
    where,
    doc,
    getDoc,
    orderBy
} from "firebase/firestore";

import { db, auth } from "../../firebase";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 280,
        },
    },
};

function Headwisecollection() {

    const [loading, setLoading] = useState(true);
    const [schoolId, setSchoolId] = useState("");

    const [heads, setHeads] = useState([]);
    const [selectedHeads, setSelectedHeads] = useState([]);

    const [fromDate, setFromDate] = useState(
        new Date().toISOString().split("T")[0]
    );

    const [toDate, setToDate] = useState(
        new Date().toISOString().split("T")[0]
    );

    const [reportData, setReportData] = useState([]);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {

            const userSnap = await getDoc(
                doc(db, "users", auth.currentUser.uid)
            );

            const sId = userSnap.data().schoolId;

            setSchoolId(sId);

            const headSnap = await getDocs(
                query(
                    collection(db, "feeheads"),
                    where("schoolId", "==", sId),
                    orderBy("sequence_no", "asc")
                )
            );

            const headList = headSnap.docs.map((d) => ({
                id: d.id,
                ...d.data()
            }));

            setHeads(headList);

        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const handleHeadChange = (event) => {
        const value = event.target.value;

        if (value.includes("all")) {
            if (selectedHeads.length === heads.length) {
                setSelectedHeads([]);
            } else {
                setSelectedHeads(heads.map((h) => h.id));
            }
        } else {
            setSelectedHeads(value);
        }
    };

    const fetchReport = async () => {

        if (selectedHeads.length === 0) {
            alert("Please select fee heads");
            return;
        }

        try {

            setFetching(true);

            const feeSnap = await getDocs(
                query(
                    collection(db, "fee_structures"),
                    where("schoolId", "==", schoolId)
                )
            );

            let rows = [];

            feeSnap.docs.forEach((docSnap) => {

                const data = docSnap.data();

                const paymentHistory = data.paymentHistory || [];
                const headsData = data.heads || [];

                paymentHistory.forEach((payment) => {

                    const payDate = payment.date;

                    if (!payDate) return;

                    if (payDate >= fromDate && payDate <= toDate) {

                        // NEW STRUCTURE
                        if (payment.headId) {

                            if (selectedHeads.includes(payment.headId)) {

                                const headInfo = headsData.find(
                                    (h) => h.headId === payment.headId
                                );

                                rows.push({
                                    studentName: data.studentName || data.fullName || "-",
                                    receiptNo: payment.receiptNo || "-",
                                    date: payment.date,
                                    payMode: payment.payMode || "-",
                                    headName: headInfo?.name || "-",
                                    amount: parseFloat(payment.paidAmount || 0)
                                });
                            }
                        }

                        // OLD STRUCTURE
                        else if (payment.multiHeads?.length > 0) {

                            payment.multiHeads.forEach((m) => {

                                if (selectedHeads.includes(m.headId)) {

                                    const headInfo = headsData.find(
                                        (h) => h.headId === m.headId
                                    );

                                    rows.push({
                                        studentName: data.studentName || data.fullName || "-",
                                        receiptNo: payment.receiptNo || "-",
                                        date: payment.date,
                                        payMode: payment.payMode || "-",
                                        headName: headInfo?.name || "-",
                                        amount: parseFloat(m.remaining || payment.paidAmount || 0)
                                    });
                                }
                            });
                        }
                    }
                });
            });

            setReportData(rows);

        } catch (err) {
            console.log(err);
        } finally {
            setFetching(false);
        }
    };

    const totalAmount = useMemo(() => {
        return reportData.reduce(
            (sum, item) => sum + (parseFloat(item.amount) || 0),
            0
        );
    }, [reportData]);

    const groupedTotals = useMemo(() => {

        const map = {};

        reportData.forEach((item) => {

            if (!map[item.headName]) {
                map[item.headName] = 0;
            }

            map[item.headName] += parseFloat(item.amount || 0);
        });

        return map;

    }, [reportData]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="70vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={1} bgcolor="#f4f7fb" minHeight="100vh">

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0"
                }}
            >

                {/* HEADER */}
                <Box
                    mb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Head Wise Collection Report
                    </Typography>

                    <Chip
                        label={`Records : ${reportData.length}`}
                        color="primary"
                    />
                </Box>

                {/* FILTERS */}
                <Grid className="row" spacing={2}>

                    <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Grid>

                    <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Grid>

                    <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Select Heads</InputLabel>

                            <Select
                                multiple
                                value={selectedHeads}
                                onChange={handleHeadChange}
                                input={<OutlinedInput label="Select Heads" />}
                                renderValue={(selected) =>
                                    heads
                                        .filter((h) => selected.includes(h.id))
                                        .map((h) => h.fee_heads)
                                        .join(", ")
                                }
                                MenuProps={MenuProps}
                            >

                                <MenuItem value="all">
                                    <Checkbox
                                        checked={
                                            heads.length > 0 &&
                                            selectedHeads.length === heads.length
                                        }
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>

                                {heads.map((head) => (
                                    <MenuItem
                                        key={head.id}
                                        value={head.id}
                                    >
                                        <Checkbox
                                            checked={selectedHeads.includes(head.id)}
                                        />

                                        <ListItemText
                                            primary={head.fee_heads}
                                        />
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid className="col-sm-3 mb-3" item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={fetchReport}
                            disabled={fetching}
                            sx={{
                                height: "40px",
                                bgcolor: "#1e293b"
                            }}
                        >
                            {fetching ? "Loading..." : "Search"}
                        </Button>
                    </Grid>

                </Grid>

                <Card
                    variant="outlined"
                    sx={{
                        mt: 3,
                        borderRadius: 3
                    }}
                >

                    <TableContainer sx={{ maxHeight: 600 }}>

                        <Table stickyHeader size="small">

                            <TableHead>
                                <TableRow>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Sr No
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Date
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Receipt No
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Student
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Head
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        Pay Mode
                                    </TableCell>

                                    <TableCell
                                        align="right"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Amount
                                    </TableCell>

                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {reportData.length === 0 ? (

                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            align="center"
                                        >
                                            No Data Found
                                        </TableCell>
                                    </TableRow>

                                ) : (

                                    <>
                                        {reportData.map((row, index) => (
                                            <TableRow key={index} hover>

                                                <TableCell>
                                                    {index + 1}
                                                </TableCell>

                                                <TableCell>
                                                    {row.date}
                                                </TableCell>

                                                <TableCell>
                                                    {row.receiptNo}
                                                </TableCell>

                                                <TableCell>
                                                    {row.studentName}
                                                </TableCell>

                                                <TableCell>
                                                    {row.headName}
                                                </TableCell>

                                                <TableCell>
                                                    {row.payMode}
                                                </TableCell>

                                                <TableCell align="right">
                                                    ₹{parseFloat(row.amount).toLocaleString()}
                                                </TableCell>

                                            </TableRow>
                                        ))}

                                        <TableRow
                                            sx={{
                                                bgcolor: "#f8fafc"
                                            }}
                                        >

                                            <TableCell
                                                colSpan={6}
                                                align="right"
                                                sx={{
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                Grand Total
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: "bold",
                                                    color: "success.main"
                                                }}
                                            >
                                                ₹{totalAmount.toLocaleString()}
                                            </TableCell>

                                        </TableRow>
                                    </>
                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>

                </Card>

            </Paper>

        </Box>
    );
}

export default Headwisecollection;